import { useState, useEffect, useCallback } from 'react'
import { PageHeader, Card, Button, Input, Select, Badge } from '@pilot-hub/ui'
import { authApi } from '@pilot-hub/api-client'
import { User, Lock, Save, RefreshCw, AlertCircle, CheckCircle, Settings as SettingsIcon } from 'lucide-react'

function ProfilTab() {
  const [daten, setDaten] = useState({ benutzername: '', vorname: '', nachname: '', email: '', telefon: '' })
  const [pw, setPw] = useState({ current_password: '', new_password: '', new_password_repeat: '' })
  const [laden, setLaden] = useState(true)
  const [speichertDaten, setSpeichertDaten] = useState(false)
  const [speichertPw, setSpeichertPw] = useState(false)
  const [fehlerDaten, setFehlerDaten] = useState<string | null>(null)
  const [erfolgDaten, setErfolgDaten] = useState<string | null>(null)
  const [fehlerPw, setFehlerPw] = useState<string | null>(null)
  const [erfolgPw, setErfolgPw] = useState<string | null>(null)

  const laden_ = useCallback(async () => {
    setLaden(true)
    try {
      const r = await authApi.me()
      const d = r.data as { benutzername?: string; vorname?: string; nachname?: string; email?: string; telefon?: string }
      setDaten({ benutzername: d.benutzername || '', vorname: d.vorname || '', nachname: d.nachname || '', email: d.email || '', telefon: d.telefon || '' })
    } catch (e: any) {
      setFehlerDaten(e?.response?.data?.detail || 'Profildaten konnten nicht geladen werden')
    } finally { setLaden(false) }
  }, [])

  useEffect(() => { laden_() }, [laden_])

  async function datenSpeichern() {
    setSpeichertDaten(true); setFehlerDaten(null); setErfolgDaten(null)
    try {
      await authApi.updateProfile({ vorname: daten.vorname, nachname: daten.nachname, email: daten.email, telefon: daten.telefon })
      setErfolgDaten('Profildaten gespeichert.')
    } catch (e: any) { setFehlerDaten(e?.response?.data?.detail || 'Speichern fehlgeschlagen') }
    finally { setSpeichertDaten(false) }
  }

  async function passwortAendern() {
    setFehlerPw(null); setErfolgPw(null)
    if (!pw.current_password || !pw.new_password) { setFehlerPw('Bitte aktuelles und neues Passwort eingeben.'); return }
    if (pw.new_password !== pw.new_password_repeat) { setFehlerPw('Die neuen Passwörter stimmen nicht überein.'); return }
    if (pw.new_password.length < 8) { setFehlerPw('Das neue Passwort muss mindestens 8 Zeichen haben.'); return }
    setSpeichertPw(true)
    try {
      await authApi.changePassword({ current_password: pw.current_password, new_password: pw.new_password, new_password_repeat: pw.new_password_repeat })
      setPw({ current_password: '', new_password: '', new_password_repeat: '' })
      setErfolgPw('Passwort geändert.')
    } catch (e: any) {
      const d = e?.response?.data?.detail
      const meldung = d === 'Current password incorrect' ? 'Das aktuelle Passwort ist falsch.'
        : d === "Passwords don't match" ? 'Die neuen Passwörter stimmen nicht überein.'
        : typeof d === 'string' ? d : 'Passwort konnte nicht geändert werden'
      setFehlerPw(meldung)
    } finally { setSpeichertPw(false) }
  }

  if (laden) return <Card className="p-6"><div className="py-8 text-center text-dark-text-muted">Lade Profil…</div></Card>

  return (
    <div className="space-y-6">
      {/* Meine Daten Section */}
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-[rgb(125_211_252/0.15)] flex items-center justify-center"><User className="w-5 h-5 text-[rgb(125_211_252)]" /></div>
          <div>
            <h2 className="text-lg font-semibold text-dark-text">Meine Daten</h2>
            <p className="text-sm text-dark-text-muted">Persönliche Informationen für Benachrichtigungen und Rückfragen</p>
          </div>
        </div>

        {fehlerDaten && <div className="mb-4 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200 flex items-center gap-2"><AlertCircle className="w-4 h-4" />{fehlerDaten}</div>}
        {erfolgDaten && <div className="mb-4 rounded-xl border border-green-500/30 bg-green-500/10 px-4 py-3 text-sm text-green-300 flex items-center gap-2"><CheckCircle className="w-4 h-4" />{erfolgDaten}</div>}

        <div className="space-y-5">
          <div>
            <label className="label flex items-center gap-2"><User className="w-4 h-4 text-dark-text-muted" />Benutzername</label>
            <Input value={daten.benutzername} disabled readOnly className="mt-1.5" />
            <p className="mt-1 text-sm text-dark-text-muted">Nicht änderbar</p>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="label">Vorname</label>
              <Input value={daten.vorname} onChange={(e) => setDaten({ ...daten, vorname: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <label className="label">Nachname</label>
              <Input value={daten.nachname} onChange={(e) => setDaten({ ...daten, nachname: e.target.value })} className="mt-1.5" />
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="label">E-Mail</label>
              <Input type="email" value={daten.email} onChange={(e) => setDaten({ ...daten, email: e.target.value })} placeholder="name@asb.de" className="mt-1.5" />
            </div>
            <div>
              <label className="label">Telefon</label>
              <Input value={daten.telefon} onChange={(e) => setDaten({ ...daten, telefon: e.target.value })} placeholder="z. B. 0151 23456789" className="mt-1.5" />
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <Button onClick={datenSpeichern} disabled={speichertDaten} className="w-full sm:w-auto !bg-[rgb(125_211_252/0.2)] !text-[rgb(125_211_252)] ring-1 ring-[rgb(125_211_252/0.55)] hover:!bg-[rgb(125_211_252/0.3)] !min-h-[48px]"><Save className="w-4 h-4 mr-2" />{speichertDaten ? 'Speichern…' : 'Speichern'}</Button>
        </div>
      </Card>

      {/* Passwort ändern Section */}
      <Card className="p-6">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-[rgb(125_211_252/0.15)] flex items-center justify-center"><Lock className="w-5 h-5 text-[rgb(125_211_252)]" /></div>
          <div>
            <h2 className="text-lg font-semibold text-dark-text">Passwort ändern</h2>
            <p className="text-sm text-dark-text-muted">Mindestens 8 Zeichen, sicher und einzigartig</p>
          </div>
        </div>

        {fehlerPw && <div className="mb-4 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200 flex items-center gap-2"><AlertCircle className="w-4 h-4" />{fehlerPw}</div>}
        {erfolgPw && <div className="mb-4 rounded-xl border border-green-500/30 bg-green-500/10 px-4 py-3 text-sm text-green-300 flex items-center gap-2"><CheckCircle className="w-4 h-4" />{erfolgPw}</div>}

        <div className="space-y-5">
          <div>
            <label className="label">Aktuelles Passwort</label>
            <Input type="password" autoComplete="current-password" value={pw.current_password} onChange={(e) => setPw({ ...pw, current_password: e.target.value })} className="mt-1.5" />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="label">Neues Passwort</label>
              <Input type="password" autoComplete="new-password" value={pw.new_password} onChange={(e) => setPw({ ...pw, new_password: e.target.value })} className="mt-1.5" />
            </div>
            <div>
              <label className="label">Neues Passwort wiederholen</label>
              <Input type="password" autoComplete="new-password" value={pw.new_password_repeat} onChange={(e) => setPw({ ...pw, new_password_repeat: e.target.value })} className="mt-1.5" />
            </div>
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <Button onClick={passwortAendern} disabled={speichertPw} className="w-full sm:w-auto !bg-[rgb(125_211_252/0.2)] !text-[rgb(125_211_252)] ring-1 ring-[rgb(125_211_252/0.55)] hover:!bg-[rgb(125_211_252/0.3)] !min-h-[48px]"><Lock className="w-4 h-4 mr-2" />{speichertPw ? 'Ändern…' : 'Passwort ändern'}</Button>
        </div>
      </Card>
    </div>
  )
}

export function SettingsPage() {
  const [tab, setTab] = useState<'profil'>('profil')

  const tabs = [
    { id: 'profil', label: 'Mein Profil', icon: User },
  ] as const

  return (
    <div className="space-y-6">
      <PageHeader title="" subtitle="Profil und Einstellungen verwalten" />

      {/* Tabs (Cyan = aktiv, ≥ 48 px) */}
      <div className="flex gap-2" role="tablist" aria-label="Einstellungen">
        {tabs.map(t => {
          const Icon = t.icon
          const aktiv = tab === t.id
          return (
            <button key={t.id} type="button" role="tab" aria-selected={aktiv} onClick={() => setTab(t.id)}
              className={`flex min-h-[48px] items-center gap-2 rounded-xl border px-5 font-semibold transition-colors ${aktiv ? 'border-[rgb(125_211_252/0.6)] bg-[rgb(125_211_252/0.18)] text-[rgb(125_211_252)]' : 'border-dark-border bg-dark-bg-tertiary text-dark-text-muted hover:text-dark-text'}`}>
              <Icon className="h-5 w-5" />
              {t.label}
            </button>
          )
        })}
      </div>

      <div role="tabpanel">
        {tab === 'profil' && <ProfilTab />}
      </div>
    </div>
  )
}
